import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Popover,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import InsertEmoticonRoundedIcon from '@mui/icons-material/InsertEmoticonRounded';
import { TFunction } from 'i18next';
import { CHAT_EMOJI_CATEGORIES, ChatEmojiCategory } from '../../utils/chatEmojis';

interface ChatEmojiPickerPanelProps {
  t: TFunction;
  categories?: readonly ChatEmojiCategory[];
  selectedEmoji?: string | null;
  onSelect: (emoji: string) => void;
}

/** Shared tabbed emoji grid used by the composer and reaction “More” picker. */
export const ChatEmojiPickerPanel: React.FC<ChatEmojiPickerPanelProps> = ({
  t,
  categories = CHAT_EMOJI_CATEGORIES,
  selectedEmoji = null,
  onSelect,
}) => {
  const [categoryIndex, setCategoryIndex] = useState(0);
  const safeIndex = Math.min(categoryIndex, Math.max(categories.length - 1, 0));
  const activeCategory = categories[safeIndex] ?? categories[0];

  if (!activeCategory) return null;

  return (
    <>
      <Tabs
        value={safeIndex}
        onChange={(_, nextIndex) => setCategoryIndex(nextIndex)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 40,
          borderBottom: 1,
          borderColor: 'divider',
          px: 0.5,
          '& .MuiTab-root': {
            minHeight: 40,
            minWidth: 72,
            px: 1,
            fontSize: '0.75rem',
            textTransform: 'none',
          },
        }}
      >
        {categories.map((category) => (
          <Tab
            key={category.id}
            label={t(category.labelKey, category.defaultLabel)}
            aria-label={t(category.labelKey, category.defaultLabel)}
          />
        ))}
      </Tabs>

      <Box
        role="listbox"
        aria-label={t('chat.timeline.emojiPicker', 'Emoji picker')}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(8, 1fr)',
          gap: 0.25,
          p: 1,
          maxHeight: 220,
          overflowY: 'auto',
        }}
      >
        {activeCategory.emojis.map((emoji) => {
          const selected = selectedEmoji === emoji;
          return (
            <IconButton
              key={`${activeCategory.id}-${emoji}`}
              role="option"
              aria-label={emoji}
              aria-selected={selected}
              onClick={() => onSelect(emoji)}
              sx={{
                width: 36,
                height: 36,
                fontSize: '1.25rem',
                borderRadius: 1,
                bgcolor: selected ? 'rgba(255,255,255,0.12)' : 'transparent',
                outline: selected ? '1px solid' : 'none',
                outlineColor: 'primary.main',
              }}
            >
              {emoji}
            </IconButton>
          );
        })}
      </Box>
    </>
  );
};

interface ChatEmojiPickerProps {
  disabled?: boolean;
  t: TFunction;
  onSelect: (emoji: string) => void;
}

const ChatEmojiPicker: React.FC<ChatEmojiPickerProps> = ({
  disabled = false,
  t,
  onSelect,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip title={t('chat.timeline.addEmoji', 'Add emoji')}>
        <span>
          <IconButton
            size="small"
            onClick={(event) => setAnchorEl(event.currentTarget)}
            disabled={disabled}
            aria-label={t('chat.timeline.addEmoji', 'Add emoji')}
            aria-expanded={open}
            aria-haspopup="dialog"
            sx={{ flexShrink: 0, alignSelf: 'flex-end', mb: 0.5, color: 'text.secondary' }}
          >
            <InsertEmoticonRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </span>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              width: 320,
              maxWidth: 'calc(100vw - 24px)',
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              boxShadow: 8,
            },
          },
        }}
      >
        <ChatEmojiPickerPanel
          t={t}
          onSelect={(emoji) => {
            onSelect(emoji);
            handleClose();
          }}
        />
      </Popover>
    </>
  );
};

export default ChatEmojiPicker;
